// import APIs
import {
  BASIC_DUTIES_BASED_ON_COST_CENTER,
  DUTY_LINES,
  DUTY_LINES_OVERVIEW,
  ROSTER_DUTY_LINE
} from 'RestAPIs/endpoints';

// import external libraries
import axios from 'axios';

// import types
import {
  BasicDutiesResponse,
  DutyLinesResponse,
  GetBasicDutiesProps,
  GetDutyLinesAuditProps,
  GetDutyLinesProps
} from './types';
import { UpdateDutyLineFlagsParams } from 'components/employee-card/employment/hours-table/hooks/useUpdateDutyLineFlags.types';
import { DeleteDutyLineMutateParams } from 'components/employee-card/employment/hours-table/hooks/useDeleteDutyLine.types';
import { MutateDutyLinesInBulkParams } from 'components/employee-card/employment/hours-table/hooks/useMutateDutyLinesInBulk.types';
import { CreateDutyLineMutateParams } from 'components/employee-card/employment/hours-table/hooks/useCreateDutyLine.types';
import { UpdateDutyLineParams } from 'components/employee-card/employment/hours-table/hooks/useUpdateDutyLine.types';
import { CUDBatchDutyLinesParams } from 'components/employee-card/employment/hours-table/hooks/useCUDBatchDutyLines.types';
import { CreditDutyLineParams } from 'components/employee-card/employment/hours-table/hooks/useCreditDutyLine.types';
import {
  CreditOperation,
  NominalHoursOperation
} from 'components/employee-card/employment/hours-table/HoursTable.enums';
import { CreateOrUpdateNominalHoursDutyLineMutateParams } from 'components/employee-card/employment/hours-table/hooks/useCreateOrUpdateNominalHoursDutyLine.types';
import { CreateDutyLineNoteMutateParams } from 'components/employee-card/employment/hours-table/hooks/useCreateDutyLineNote.types';
import { isArray } from 'lodash';

export const getDutyLines = ({
  employeeId,
  startDate,
  endDate,
  showUnpublished
}: GetDutyLinesProps) => {
  return axios.get<DutyLinesResponse>(DUTY_LINES_OVERVIEW, {
    pathParams: {
      employeeId,
      startDate,
      endDate,
      showUnpublished: String(showUnpublished)
    }
  });
};

export const getBasicDutiesBasedOnCostCenter = ({
  employeeId: username,
  startDate,
  endDate,
  costCenters
}: GetBasicDutiesProps) =>
  axios.get<BasicDutiesResponse[]>(BASIC_DUTIES_BASED_ON_COST_CENTER, {
    pathParams: { username },
    params: {
      startDate,
      endDate,
      ...(costCenters ? { costCenters } : {})
    }
  });

export const updateDutyLineFlags = ({
  employeeId,
  requestId,
  flags,
  operation
}: UpdateDutyLineFlagsParams) =>
  axios.put(
    `${ROSTER_DUTY_LINE}/${requestId}/flags`,
    { flags, operation },
    {
      pathParams: {
        username: employeeId
      }
    }
  );

export const deleteDutyLine = ({
  employeeId,
  requestId
}: DeleteDutyLineMutateParams) =>
  axios.delete(`${ROSTER_DUTY_LINE}/${requestId}`, {
    pathParams: {
      username: employeeId
    }
  });

export const mutateDutyLinesInBulk = ({
  employeeId,
  dutyLineIds,
  type,
  flags
}: MutateDutyLinesInBulkParams) => {
  if (type === 'add' && flags && dutyLineIds) {
    return axios.put(
      `${ROSTER_DUTY_LINE}/flags-bulk`,
      { dutyLineIds, flags, operation: type },
      {
        pathParams: {
          username: employeeId
        }
      }
    );
  } else {
    return axios.delete(ROSTER_DUTY_LINE, {
      pathParams: {
        username: employeeId
      },
      params: {
        dutyLineIds: isArray(dutyLineIds) ? dutyLineIds.join(',') : dutyLineIds,
        deleteType: type
      }
    });
  }
};

export const getDutyLinesAudit = ({
  employeeId,
  requestId
}: GetDutyLinesAuditProps) => {
  return axios.get<DutyLinesResponse>(
    `${ROSTER_DUTY_LINE}/${requestId}/audit`,
    {
      pathParams: {
        username: employeeId
      }
    }
  );
};
export const getDutyLinesPeriodsAudit = ({
  employeeId,
  requestId
}: GetDutyLinesAuditProps) => {
  return axios.get<DutyLinesResponse>(
    `${ROSTER_DUTY_LINE}/${requestId}/periods/audit`,
    {
      pathParams: {
        username: employeeId
      }
    }
  );
};

export const createDutyLine = ({
  employeeId,
  body
}: CreateDutyLineMutateParams) =>
  axios.post(`${ROSTER_DUTY_LINE}/no-request`, body, {
    pathParams: {
      username: employeeId
    }
  });

export const updateDutyLine = ({
  employeeId,
  requestId,
  body
}: UpdateDutyLineParams) =>
  axios.put(`${ROSTER_DUTY_LINE}/${requestId}/details`, body, {
    pathParams: {
      username: employeeId
    }
  });

export const cudBatchDutyLines = ({
  employeeId,
  body
}: CUDBatchDutyLinesParams) =>
  axios.post(`${DUTY_LINES}`, body, {
    pathParams: {
      username: employeeId
    }
  });

export const creditDutyLine = ({
  employeeId,
  requestId,
  operation
}: CreditDutyLineParams) =>
  operation === CreditOperation.CREDITING
    ? axios.post(`${ROSTER_DUTY_LINE}/${requestId}/credit`, {
        pathParams: {
          username: employeeId
        }
      })
    : axios.delete(`${ROSTER_DUTY_LINE}/${requestId}/credit`, {
        pathParams: {
          username: employeeId
        }
      });

export const createOrUpdateNominalHoursDutyLine = ({
  employeeId,
  body,
  requestId,
  operation = NominalHoursOperation.CREATE
}: CreateOrUpdateNominalHoursDutyLineMutateParams) =>
  operation === NominalHoursOperation.CREATE
    ? axios.post(`${ROSTER_DUTY_LINE}/nominal`, body, {
        pathParams: {
          username: employeeId
        }
      })
    : axios.put(`${ROSTER_DUTY_LINE}/${requestId}/nominal`, body, {
        pathParams: {
          username: employeeId
        }
      });

export const createDutyLineNote = ({
  employeeId,
  dutyLineId,
  body
}: CreateDutyLineNoteMutateParams) =>
  axios.post(`${ROSTER_DUTY_LINE}/${dutyLineId}/note`, body, {
    pathParams: {
      username: employeeId
    }
  });
